import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AdminConsoleUsersApi } from '@portal/pkg-api/admin-console'
import { ImpersonationApi, UserExportApi } from '@portal/pkg-api/admin'
import { data } from '@portal/pkg-api/helpers'
import { filenameFromContentDisposition } from '@portal/pkg-utils'
import type { UpdateUserRequest } from '@portal/pkg-api/gen/admin-console'
import type { EditUserFormValues } from './model/editUserSchema'
import { UserQueries } from './queries'
import { AddUserFormValues, IntegrationRoleOption } from './model/addUserSchema'
import { UserStatus } from '@portal/pkg-api/gen/admin-console'

const prefix = 'admin-console.users'

function toIntegrationsRole(value: string): string | null {
  return value === IntegrationRoleOption.NOT_SELECTED ? null : value
}

export const UserMutations = {
  updateStatus: (orgId?: number) => ({
    mutationKey: [prefix, 'updateStatus'],
    mutationFn: ({ id, status }: { id: number; status: UserStatus }) =>
      data(
        AdminConsoleUsersApi.updateUserStatus(id, { status }, orgId, {
          mercatusNotifyOnError: false,
        }),
      ),
  }),
  impersonate: () => ({
    mutationKey: [prefix, 'impersonate'],
    mutationFn: ({ userId }: { userId: number }) => data(ImpersonationApi.impersonateUser(userId)),
  }),
  editUser: (orgId?: number) => ({
    mutationKey: [prefix, 'editUser'],
    mutationFn: ({ id, ...values }: { id: number } & EditUserFormValues) => {
      const request: UpdateUserRequest = {
        givenName: values.firstName,
        familyName: values.lastName,
        licenseType: values.licenseType,
        integrationsRole: toIntegrationsRole(values.integrationsRole),
        notificationFrequency: values.notificationFrequency,
        projectParticipant: values.projectParticipant,
        emailNotifications: values.emailNotifications,
      }
      return data(
        AdminConsoleUsersApi.updateUser(id, request, orgId, { mercatusNotifyOnError: false }),
      )
    },
  }),
  exportCsv: () => ({
    mutationKey: [prefix, 'exportCsv'],
    mutationFn: async (orgId?: number) => {
      // The 'exportUsersCsv' endpoint returns a CSV starting with a 3-byte BOM (byte order mark)
      // 0xEF 0xBB 0xBF so that Excel will recognize the file as UTF-8. By default, Axios strips the
      // BOM during decoding. We set the response type to 'arraybuffer' to get the raw bytes with
      // the BOM intact.
      const response = await UserExportApi.exportUsersCsv(orgId, {
        responseType: 'arraybuffer',
      })
      const headers = response.headers as Record<string, string> | undefined
      const filename =
        filenameFromContentDisposition(headers?.['content-disposition']) ?? 'users.csv'
      return { content: response.data as unknown as ArrayBuffer, filename }
    },
  }),
  createUser: (orgId?: number) => ({
    mutationKey: [prefix, 'createUser'],
    mutationFn: async (formValues: AddUserFormValues) => {
      const request = {
        ...formValues,
        integrationsRole: toIntegrationsRole(formValues.integrationsRole),
      }
      const options: Record<string, unknown> = { mercatusNotifyOnError: false }
      if (orgId !== undefined) options.query = { __orgId__: orgId }
      const response = await AdminConsoleUsersApi.createUser(request, options)
      return { content: response.data }
    },
  }),
}

export function useUpdateUserStatus(orgId?: number) {
  const queryClient = useQueryClient()
  return useMutation({
    ...UserMutations.updateStatus(orgId),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries(UserQueries.list(orgId))
      void queryClient.invalidateQueries(UserQueries.detail(id, orgId))
    },
  })
}

export function useImpersonateUser() {
  return useMutation({
    ...UserMutations.impersonate(),
    onSuccess: () => {
      // The impersonated user may not be allowed on this page, and it belongs to the
      // administrator's session, so replace it with the home page.
      window.location.replace('/')
    },
  })
}

export function useEditUser(orgId?: number) {
  const queryClient = useQueryClient()
  return useMutation({
    ...UserMutations.editUser(orgId),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries(UserQueries.list(orgId))
      void queryClient.invalidateQueries(UserQueries.detail(id, orgId))
    },
  })
}

export function useExportUsersCsv() {
  return useMutation({
    ...UserMutations.exportCsv(),
    onSuccess: ({ content, filename }) => {
      const blob = new Blob([content], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
      URL.revokeObjectURL(url)
    },
  })
}

export function useCreateUser(orgId?: number) {
  const queryClient = useQueryClient()
  return useMutation({
    ...UserMutations.createUser(orgId),
    onSuccess: () => {
      void queryClient.invalidateQueries(UserQueries.list(orgId))
    },
  })
}
